<script setup lang="ts">
  import { ref } from "vue";
  import { IconChevronRight, IconClose, Button, IconChevronDown, IconChevronUp } from "@ui-kit";
  import { useProfileOptions, useLogging } from "@src/hooks";
  import { default as LogData } from "./LogData.vue";

  const { logs, clearLogs } = useLogging();
  const { hasPermission } = useProfileOptions();

  const visible = ref(false);
  const expandedLogs = ref<Set<string>>(new Set());

  const toggleLog = (timestamp: string) => {
    if (expandedLogs.value.has(timestamp)) {
      expandedLogs.value.delete(timestamp);
    } else {
      expandedLogs.value.add(timestamp);
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };
</script>

<template>
  <Teleport to="body">
    <div v-if="hasPermission('mobile.debug')" class="logging">
      <div class="logging__buttons">
        <Button
          asSquare
          variant="secondary"
          size="s"
          :iconBefore="visible ? IconChevronUp : IconChevronDown"
          @click="visible = !visible"
        />
        <Button asSquare variant="secondary" size="s" :iconBefore="IconClose" @click="clearLogs" />
      </div>
      <div v-if="visible" class="logging__list">
        <div
          v-for="log in logs"
          :key="log.timestamp"
          class="logging__item"
          :class="{ 'error': log.status === 'error' }"
        >
          <div class="logging__header" @click="toggleLog(log.timestamp)">
            <IconChevronRight
              class="logging__chevron"
              :class="{ 'logging__chevron--expanded': expandedLogs.has(log.timestamp) }"
            />
            <div class="logging__title">
              <span>{{ log.url }}</span>
              <span class="logging__timestamp">{{ formatTime(log.timestamp) }}</span>
            </div>
          </div>
          <div v-if="expandedLogs.has(log.timestamp)" class="logging__content">
            <div v-if="log.isString" class="logging__data-value">{{ log.data }}</div>
            <LogData v-else :data="log.data" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
  .logging__buttons {
    position: absolute;
    z-index: 1000;
    top: 7px;
    left: 0;
    display: flex;
    gap: 2px;
    width: 100%;
    justify-content: center;
  }

  .logging__buttons :deep(.button) {
    width: 30px;
    height: 30px;
  }

  .logging__list {
    position: absolute;
    z-index: 1000;
    top: 40px;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--sf-base-background-white);
    overflow-y: auto;
  }

  .logging__item {
    border-bottom: 1px solid var(--sf-base-border-secondary);
    padding: 4px 8px;

    &.error {
      background-color: var(--sf-base-background-error);
    }
  }

  .logging__header {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  .logging__title {
    display: flex;
    gap: 12px;
    align-items: center;
    flex: 1;
    justify-content: space-between;
    overflow: hidden;
  }

  .logging__title span:first-of-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .logging__timestamp {
    white-space: nowrap;
    color: var(--sf-base-text-secondary);
    font-size: 0.9em;
  }

  .logging__chevron {
    transition: transform 0.2s;
  }

  .logging__chevron--expanded {
    transform: rotate(90deg);
  }

  .logging__content {
    padding: 8px;
    background-color: var(--sf-base-background-card);
    border-radius: 4px;
  }

  .logging__data-row {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
  }

  .logging__data-key {
    font-weight: 500;
    color: var(--sf-base-text-secondary);
  }

  .logging__data-value {
    word-break: break-all;
  }
</style>
